
import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { signOut, checkEmailVerificationStatus } from '@/services/auth.service';
import { isSuperAdmin } from '@/services/admin.service';
import { UserRole } from '@/types/user-roles';
import { AuthError, User as SupabaseUser, Session } from '@supabase/supabase-js';
import { cleanupAuthState } from '@/utils/authCleanup';
import { identifyUser, resetPosthog } from '@/analytics/posthog';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role?: UserRole;
  emailVerified?: boolean;
}

interface AuthContextType {
  user: User | null;
  userRole: UserRole | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isEmailVerified: boolean;
  needsProfileCompletion: boolean;
  logout: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{
    data: { user: SupabaseUser | null };
    error: AuthError | null;
  }>;
  refreshEmailVerificationStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  userRole: null,
  session: null,
  isAuthenticated: false,
  isLoading: true,
  isEmailVerified: false,
  needsProfileCompletion: false,
  logout: async () => {},
  signIn: async () => ({ data: { user: null }, error: null }),
  refreshEmailVerificationStatus: async () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [needsProfileCompletion, setNeedsProfileCompletion] = useState(false);

  const refreshEmailVerificationStatus = async () => {
    try {
      // Get current session without triggering auth state change
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (currentSession?.user?.id) {
        const verified = await checkEmailVerificationStatus(currentSession.user.id);
        setIsEmailVerified(verified);
        setUser(prev => prev ? { ...prev, emailVerified: verified } : null);
        console.log('Email verification status refreshed:', verified);
      }
    } catch (error) {
      console.error('Error refreshing email verification:', error);
    }
  };

  useEffect(() => {
    console.log("AuthProvider: Initializing auth state");
    
    // Configure Supabase client with explicit auth settings
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        console.log("AuthProvider: Auth state changed", { event, hasSession: !!currentSession });
        
        // Always update session state first
        setSession(currentSession);
        
        if (currentSession?.user) {
          console.log("AuthProvider: User authenticated", { 
            id: currentSession.user.id,
            email: currentSession.user.email
          });
          
          // Set user immediately for OAuth to prevent redirect loops
          const tempUserData = {
            id: currentSession.user.id,
            email: currentSession.user.email || '',
            firstName: currentSession.user.user_metadata?.first_name || 
                      currentSession.user.user_metadata?.given_name,
            lastName: currentSession.user.user_metadata?.last_name || 
                     currentSession.user.user_metadata?.family_name,
            role: (currentSession.user.user_metadata?.role as UserRole) || 'regular',
          };
          setUser(tempUserData);
          setUserRole(tempUserData.role);

          // Fetch current user type from profiles table (async but non-blocking)
          (async () => {
            try {
              let { data: profile, error } = await supabase
                .from('profiles')
                .select('user_type, first_name, last_name')
                .eq('id', currentSession.user.id)
                .single();

              if (error) {
                console.error('Error fetching user profile:', error);
                
                // For OAuth users with missing profiles, try to create the profile
                if (currentSession.user.email_confirmed_at || currentSession.user.app_metadata?.provider) {
                  console.log('OAuth user with missing profile, attempting to create...');
                  try {
                    const firstName = currentSession.user.user_metadata?.first_name || 
                                    currentSession.user.user_metadata?.given_name ||
                                    (currentSession.user.user_metadata?.full_name?.split(' ')[0]);
                    const lastName = currentSession.user.user_metadata?.last_name || 
                                   currentSession.user.user_metadata?.family_name ||
                                   (currentSession.user.user_metadata?.full_name?.split(' ')[1]);
                    
                    await supabase
                      .from('profiles')
                      .insert({
                        id: currentSession.user.id,
                        email: currentSession.user.email,
                        first_name: firstName,
                        last_name: lastName,
                        user_type: currentSession.user.user_metadata?.role || 'regular',
                        email_verified: !!currentSession.user.email_confirmed_at,
                      });
                    
                    console.log('Successfully created missing profile for OAuth user');
                    
                    // Retry fetching the profile
                    const { data: newProfile } = await supabase
                      .from('profiles')
                      .select('user_type, first_name, last_name')
                      .eq('id', currentSession.user.id)
                      .single();
                    
                    if (newProfile) {
                      profile = newProfile;
                    }
                  } catch (createError) {
                    console.error('Failed to create missing profile:', createError);
                  }
                }
                
                if (!profile) {
                  // Profile already set above with temp data
                  console.log('Using fallback user data from OAuth metadata');
                  return;
                }
              }

              // Check for pending signup data from OAuth flow
              const pendingData = localStorage.getItem('pendingSignupData');
              let needsUpdate = false;
              
              if (pendingData && !currentSession.user.user_metadata?.profile_completed) {
                try {
                  const signupData = JSON.parse(pendingData);
                  console.log('Found pending signup data:', signupData);
                  
                  // Update user metadata with pending data
                  await supabase.auth.updateUser({
                     data: {
                       role: signupData.role,
                       terms_agreed: signupData.terms_agreed,
                       terms_agreed_at: signupData.terms_agreed_at,
                       profile_completed: true,
                       organization_name: signupData.organizationName
                     }
                   });
                  
                   // Update profile with role and organization name
                   await supabase
                     .from('profiles')
                     .update({ 
                       user_type: signupData.role,
                       organization_name: signupData.organizationName
                     })
                     .eq('id', currentSession.user.id);
                  
                  // If user is signing up as PEO, trigger verification process
                  if (signupData.role === 'peo') {
                    try {
                      console.log('Triggering PEO verification for OAuth user');
                      const adminUrl = `${window.location.origin}/admin/peo-verifications`;
                      
                      // Send PEO verification request email
                      const { error: peoNotifyError } = await supabase.functions.invoke('send-email', {
                        body: {
                          type: 'peo_verification_request',
                          userData: {
                            email: currentSession.user.email,
                            firstName: profile?.first_name || currentSession.user.user_metadata?.first_name,
                            lastName: profile?.last_name || currentSession.user.user_metadata?.last_name,
                            role: signupData.role,
                            userId: currentSession.user.id,
                            signupDate: new Date().toISOString(),
                            requestedAt: new Date().toISOString(),
                            adminUrl,
                            provider: signupData.provider || 'oauth'
                          }
                        }
                      });
                      
                      if (peoNotifyError) {
                        console.error('Error sending PEO verification request email for OAuth user:', peoNotifyError);
                      } else {
                        console.log('PEO verification request sent successfully for OAuth user');
                      }
                    } catch (verificationError) {
                      console.error('Error triggering PEO verification for OAuth user:', verificationError);
                      // Don't fail the auth flow if verification email fails
                    }
                  }
                  
                  // Clear pending data
                  localStorage.removeItem('pendingSignupData');
                  needsUpdate = true;
                } catch (error) {
                  console.error('Error processing pending signup data:', error);
                  localStorage.removeItem('pendingSignupData');
                }
              }

              // Update with actual profile data from database
              const userData = {
                id: currentSession.user.id,
                email: currentSession.user.email || '',
                firstName: profile?.first_name || currentSession.user.user_metadata?.first_name,
                lastName: profile?.last_name || currentSession.user.user_metadata?.last_name,
                role: (profile?.user_type as UserRole) || tempUserData.role,
              };
              
              // Check if profile needs completion (missing role or terms not agreed)
              const hasTermsAgreed = currentSession.user.user_metadata?.terms_agreed || 
                                   currentSession.user.user_metadata?.profile_completed ||
                                   pendingData; // If we just processed pending data
              const hasValidRole = profile?.user_type && profile.user_type !== 'regular';
              
              // Check if user is an admin - admins skip profile completion
              let isAdmin = false;
              try {
                isAdmin = await isSuperAdmin(currentSession.user.id);
              } catch (error) {
                console.log('Admin check failed, continuing with normal flow:', error);
              }
              
              const profileIncomplete = !isAdmin && (!hasTermsAgreed || !hasValidRole);
              
              console.log("AuthProvider: Profile loaded", { 
                userType: profile?.user_type,
                role: userData.role,
                hasTermsAgreed,
                hasValidRole,
                profileIncomplete,
                needsUpdate
              });
              
              // Update with final profile data
              setUser(userData);
              setUserRole(userData.role);
              setNeedsProfileCompletion(profileIncomplete && !needsUpdate);
              
              // Identify user in PostHog
              identifyUser(userData.id, {
                email: userData.email,
                firstName: userData.firstName,
                lastName: userData.lastName,
                role: userData.role,
                emailVerified: true, // Will be updated by email verification check
              });
            } catch (error) {
              console.error('Error in profile fetch:', error);
            }
          })();
          
          // Check if user authenticated via phone (SMS users are considered verified)
          const isPhoneUser = currentSession.user.phone && 
                             (currentSession.user.app_metadata?.provider === 'phone' || 
                              !currentSession.user.email);
          
          // Check if user is verified through OAuth or phone
          const isOAuthUser = currentSession.user.email_confirmed_at && 
                              currentSession.user.app_metadata?.provider &&
                              currentSession.user.app_metadata.provider !== 'email';
          
          if (isPhoneUser) {
            console.log('Phone user detected, skipping email verification');
            setIsEmailVerified(true);
          } else if (isOAuthUser) {
            console.log('OAuth user detected with confirmed email, skipping additional verification');
            setIsEmailVerified(true);
          } else {
            // Check email verification status immediately for better UX
            if (currentSession.user?.id) {
              checkEmailVerificationStatus(currentSession.user.id)
                .then(verified => {
                  setIsEmailVerified(verified);
                  setUser(prev => prev ? { ...prev, emailVerified: verified } : prev);
                })
                .catch(error => {
                  console.error('Error checking email verification:', error);
                  // For existing users, assume verified if check fails
                  setIsEmailVerified(true);
                });
            }
          }
        } else {
          console.log("AuthProvider: No user session");
          setUser(null);
          setUserRole(null);
          setIsEmailVerified(false);
          setNeedsProfileCompletion(false);
          
          // Reset PostHog on logout
          resetPosthog();
        }
        
        setIsLoading(false);
      }
    );

    // Check for existing session after setting up listener
    const checkInitialSession = async () => {
      try {
        console.log("AuthProvider: Checking initial user session");
        const { data } = await supabase.auth.getSession();
        
        if (data.session?.user) {
          // The onAuthStateChange will handle this, but we set loading to false if no session
          console.log("AuthProvider: Initial session found");
        } else {
          console.log("AuthProvider: No initial user session");
          setIsLoading(false);
        }
      } catch (error) {
        console.error("AuthProvider: Error checking user session:", error);
        setIsLoading(false);
      }
    };

    checkInitialSession();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const logout = async () => {
    try {
      console.log("AuthProvider: Logging out user");
      
      // Clean up auth state first
      cleanupAuthState();
      
      // Attempt global sign out
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (err) {
        console.error("Error during signout, continuing with cleanup:", err);
      }
      
      // Reset state
      setUser(null);
      setUserRole(null);
      setSession(null);
      setIsEmailVerified(false);
      setNeedsProfileCompletion(false);
      
      // Reset PostHog user tracking
      resetPosthog();
      
      // Force page reload for clean state
      window.location.href = '/';
    } catch (error) {
      console.error("AuthProvider: Logout error:", error);
      // Force reload even on error
      window.location.href = '/';
    }
  };

  const signIn = async (email: string, password: string) => {
    // Clean up any existing auth state before signing in
    cleanupAuthState();
    
    try {
      // Attempt global sign out first
      await supabase.auth.signOut({ scope: 'global' });
    } catch (err) {
      // Continue even if this fails
      console.log("Pre-signin cleanup completed");
    }
    
    return await supabase.auth.signInWithPassword({
      email,
      password
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userRole,
        session,
        isAuthenticated: !!session,
        isLoading,
        isEmailVerified,
        needsProfileCompletion,
        logout,
        signIn,
        refreshEmailVerificationStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
